/** @jsx jsx */
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { GatsbyImage } from 'gatsby-plugin-image'
import { Box, Button, Flex, Grid, Heading, jsx, Text } from 'theme-ui'
import {
  useAnalytics,
  useTranslate,
} from '@chordcommerce/gatsby-theme-autonomy'
import RichTextRenderer from '~/components/Content/RichText'
import FormInputWithLabel from '~/components/Generic/Form/InputWithLabel'
import { useAria } from '~/hooks/components/use-aria'
import { addUserToKlaviyoList } from '~/services/klaviyo/client'

const KlaviyoEmailCaptureSection = ({
  description,
  footer,
  formId,
  images,
  success,
  title,
}) => {
  const { trackEmailCaptured } = useAnalytics()
  const { updateAriaLive } = useAria()
  const { register, handleSubmit, errors } = useForm()
  const [submitted, setSubmitted] = useState(false)
  const translate = useTranslate()

  const onSubmit = async (data) => {
    const { email } = data

    try {
      addUserToKlaviyoList(email, formId)
      trackEmailCaptured({
        emailCapture: {
          email,
          page: 'mothers-day-giveaway',
          component: 'klaviyo-email-capture',
          website: window.location.hostname,
        },
      })
      setSubmitted(true)
      updateAriaLive('Your submission was successful.')
    } catch (err) {
      console.error(err, { source: 'MothersDayGiveawaySignup' })
    }
  }

  return (
    <Box>
      <Grid columns={[1, 12]} sx={{ rowGap: 0 }}>
        <Flex
          pb={[5, null, 7]}
          pl={[2, 0]}
          pr={[2, 0, 3]}
          pt={[5, null, 6]}
          sx={{
            flexDirection: 'column',
            gridColumn: [null, '3 / 11', '7 / -1'],
            gridRow: [null, '1 / 2'],
            justifyContent: 'center',
            maxWidth: [null, null, '573px'],
          }}
        >
          <Heading as="h1" variant="title2">
            {title}
          </Heading>
          {description && (
            <Box
              sx={{
                variant: 'text.body2',
                a: {
                  variant: 'text.link',
                },
              }}
              dangerouslySetInnerHTML={{
                __html: description.childMarkdownRemark.html,
              }}
            />
          )}

          {!submitted && (
            <Box>
              <Flex
                as="form"
                onSubmit={handleSubmit(onSubmit)}
                sx={{ flexDirection: ['column', 'row'], width: '100%' }}
              >
                <Box sx={{ flexGrow: '1' }}>
                  <FormInputWithLabel
                    name="email"
                    title="Email address"
                    label="Email address"
                    placeholder="Email address"
                    aria-label="Email address"
                    ref={register({
                      required: true,
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: translate('validation.invalid_email'),
                      },
                    })}
                    mb={[2, 0]}
                  />
                </Box>
                <Button type="submit" variant="primary" ml={[0, 1]}>
                  {'Enter'}
                </Button>
              </Flex>
              {errors && errors['email'] && (
                <Text as="p" variant="error" mt={2} sx={{ color: 'error' }}>
                  {errors['email'].type === 'required' &&
                    translate('validation.required_field')}

                  {errors['email'].type === 'pattern' &&
                    translate('validation.invalid_email')}
                </Text>
              )}
            </Box>
          )}

          {submitted && (
            <Text as="p" variant="body1" sx={{ fontWeight: 'bold' }}>
              <RichTextRenderer richText={success} optionSet="text" />
            </Text>
          )}

          {footer && (
            <Box
              sx={{
                variant: 'text.finePrint',
                color: 'greyDark',
                a: {
                  variant: 'text.link',
                },
              }}
              dangerouslySetInnerHTML={{
                __html: footer.childMarkdownRemark.html,
              }}
            />
          )}
        </Flex>
        {images && (
          <Flex
            sx={{
              gridColumn: [null, '1 / -1', '1 / 7'],
              gridRow: [null, '2 / 3', '1 / 2'],
            }}
          >
            {images.map((image, index) => (
              <GatsbyImage
                image={image.gatsbyImageData}
                key={`image-${index}`}
                alt={image.description}
                style={{ height: 'auto', width: '100%' }}
              />
            ))}
          </Flex>
        )}
      </Grid>
    </Box>
  )
}

export default KlaviyoEmailCaptureSection
